import type { ContentStatuses, GameType } from '@sparted/shared-library/business/types';
import { LanguageCode, MultilingualLabel } from '@sparted/shared-library/business/types/multilingual';
import { t } from 'i18next';
import { set } from 'lodash';

import { TranslationSection } from 'Components/structural/STranslationInterface/STranslationInterface';
import { getLanguageFromCode } from 'Services/language/languageService';

import { CONTENT_TITLE_CHAR_LIMIT, KNOW_MORE_CHAR_LIMIT } from '../GameplaysForm/gameplay.constants';
import { ContentFormValues } from './contentForm.hook';
import {
  handleTranslateErrorText,
  handleTranslateMCQ,
  handleTranslateNoInteraction,
  handleTranslateOpenQuestion,
  handleTranslateOrderingCards,
  handleTranslatePictureSpot,
  handleTranslateSwipingCards,
} from './gameplayTranslationHandler';

export const getContentId = () =>
  window.location.pathname.split('/').slice(-1)[0] === 'new'
    ? null
    : parseInt(window.location.pathname.split('/').slice(-1)[0]);

export const formatContentForTranslationInterface = (content: ContentFormValues): TranslationSection[] => {
  if (!content || !content.game || !content.game.type) return [];

  const metadataSection: TranslationSection = {
    items: [
      {
        label: t('contents:edition.label'),
        value: content.name,
        name: 'name',
        maxCharacter: CONTENT_TITLE_CHAR_LIMIT,
      },
      {
        label: t('contents:edition.knowMore'),
        value: content?.knowMore || undefined,
        name: 'knowMore',
        maxCharacter: KNOW_MORE_CHAR_LIMIT,
        type: 'textarea',
      },
    ],
  };

  switch (content.game?.type) {
    case 'mcq': {
      return handleTranslateMCQ(content, metadataSection);
    }

    case 'error-text': {
      return handleTranslateErrorText(content, metadataSection);
    }

    case 'open-question': {
      return handleTranslateOpenQuestion(content, metadataSection);
    }

    case 'no-interaction': {
      return handleTranslateNoInteraction(content, metadataSection);
    }

    case 'picture-spot': {
      return handleTranslatePictureSpot(content, metadataSection);
    }

    case 'ordering-cards': {
      return handleTranslateOrderingCards(content, metadataSection);
    }

    case 'swiping-cards': {
      return handleTranslateSwipingCards(content, metadataSection);
    }

    default: {
      return [metadataSection];
    }
  }
};

type ExtraValuesType = Record<string, string | number | boolean | null | undefined> | undefined;
export const formatTranslationInterfaceForContent = (
  translations: TranslationSection[],
  gameplayType: GameType,
): Partial<ContentFormValues> => {
  const flattenedTranslations = translations.reduce(
    (
      acc: {
        value?: Partial<MultilingualLabel> | number | string;
        name: string;
        extraValues?: ExtraValuesType;
      }[],
      section,
    ) => [...acc, ...section.items],
    [],
  );

  const flattenedTranslationsAndFields = flattenedTranslations.reduce(
    (
      acc: Record<
        string,
        {
          value?: Partial<MultilingualLabel> | number | string;
          extraValues?: ExtraValuesType;
        }
      >,
      section,
    ) => ({
      ...acc,
      [section.name]: {
        value: section.value,
        extraValues: section.extraValues,
      },
    }),
    {},
  );

  switch (gameplayType) {
    case 'mcq': {
      return {
        name: flattenedTranslationsAndFields.name?.value as MultilingualLabel,
        knowMore: flattenedTranslationsAndFields.knowMore?.value as MultilingualLabel,
        game: {
          question: flattenedTranslationsAndFields.question?.value as MultilingualLabel,
          choices: flattenedTranslations
            .filter((field) => field.name.includes('choices'))
            .map((field) => {
              return {
                text: field.value as MultilingualLabel,
                ...field.extraValues,
              };
            }),
          explanation: flattenedTranslationsAndFields.explanation?.value as MultilingualLabel,
        },
      };
    }

    case 'error-text': {
      return {
        name: flattenedTranslationsAndFields.name?.value as MultilingualLabel,
        knowMore: flattenedTranslationsAndFields.knowMore?.value as MultilingualLabel,
        game: {
          correctText: flattenedTranslationsAndFields.correctText?.value as MultilingualLabel,
          wrongText: flattenedTranslationsAndFields.wrongText?.value as MultilingualLabel,
          explanation: flattenedTranslationsAndFields.explanation?.value as MultilingualLabel,
        },
      };
    }

    case 'open-question': {
      return {
        name: flattenedTranslationsAndFields.name?.value as MultilingualLabel,
        knowMore: flattenedTranslationsAndFields.knowMore?.value as MultilingualLabel,
        game: {
          question: flattenedTranslationsAndFields.question?.value as MultilingualLabel,
          answer: flattenedTranslationsAndFields.answer?.value as MultilingualLabel,
          explanation: flattenedTranslationsAndFields.explanation?.value as MultilingualLabel,
        },
      };
    }

    case 'no-interaction': {
      return {
        name: flattenedTranslationsAndFields.name?.value as MultilingualLabel,
        knowMore: flattenedTranslationsAndFields.knowMore?.value as MultilingualLabel,
        game: {
          title: flattenedTranslationsAndFields.title?.value as MultilingualLabel,
          text: flattenedTranslationsAndFields.text?.value as MultilingualLabel,
        },
      };
    }

    case 'picture-spot': {
      // the type is lost between the set and the translation interface, it is possible to type it better but i'm running out of time
      // items is supposed to match the type of the game.items field
      const items: any = {};

      Object.entries(flattenedTranslationsAndFields)
        .filter(([key]) => key.includes('items'))
        .map(([key, value]) => {
          set(items, key, value.value);
        });

      return {
        name: flattenedTranslationsAndFields.name?.value as MultilingualLabel,
        knowMore: flattenedTranslationsAndFields.knowMore?.value as MultilingualLabel,
        game: {
          items: items.items,
          explanation: flattenedTranslationsAndFields.explanation?.value as MultilingualLabel,
        },
      };
    }

    case 'ordering-cards': {
      return {
        name: flattenedTranslationsAndFields.name?.value as MultilingualLabel,
        knowMore: flattenedTranslationsAndFields.knowMore?.value as MultilingualLabel,
        game: {
          question: flattenedTranslationsAndFields.question?.value as MultilingualLabel,
          explanation: flattenedTranslationsAndFields.explanation?.value as MultilingualLabel,
          topLabel: flattenedTranslationsAndFields.topLabel?.value as MultilingualLabel,
          bottomLabel: flattenedTranslationsAndFields.bottomLabel?.value as MultilingualLabel,
          cards: flattenedTranslations
            .filter((field) => field.name.includes('cards'))
            .map((field) => {
              return field.value as MultilingualLabel;
            }),
        },
      };
    }

    case 'swiping-cards': {
      return {
        name: flattenedTranslationsAndFields.name?.value as MultilingualLabel,
        knowMore: flattenedTranslationsAndFields.knowMore?.value as MultilingualLabel,
        game: {
          question: flattenedTranslationsAndFields.question?.value as MultilingualLabel,
          zones: flattenedTranslations
            .filter((field) => field.name.includes('zones'))
            .map((field) => {
              return {
                text: field.value as MultilingualLabel,
                ...field.extraValues,
              };
            }),
          cards: flattenedTranslations
            .filter((field) => field.name.includes('cards'))
            .map((field) => {
              return {
                text: field.value as MultilingualLabel,
                ...field.extraValues,
              };
            }),
          explanation: flattenedTranslationsAndFields.explanation?.value as MultilingualLabel,
        },
      };
    }

    default: {
      return {};
    }
  }
};

export const getFirstValidatedLanguage = (statuses: ContentStatuses) => {
  const languageCode = Object.entries(statuses).find(([, status]) => status === 'validated')?.[0] as LanguageCode;
  return languageCode ? getLanguageFromCode(languageCode) : undefined;
};
